import { subDays } from 'date-fns';

import { type ClipsListOptions, type SearchFilterFields, SortDirection, type ViewField } from '@air/api/types';

import { isCustomFieldValueFilter } from '~/utils/filters/isCustomFieldValueFilter';
import { toDateOnly } from '~/utils/internalAnalytics/metrics';

const INTERNAL_ANALYTICS_TABLE_FIELD_NAMES = ['views', 'downloads'] as const;

type InternalAnalyticsTableFieldName = (typeof INTERNAL_ANALYTICS_TABLE_FIELD_NAMES)[number];

const METRIC_SORT_FALLBACK = { sortFieldName: 'dateModified', sortDirection: SortDirection.desc } as const;

export const isInternalAnalyticsTableFieldName = (
  fieldName: string | undefined,
): fieldName is InternalAnalyticsTableFieldName =>
  INTERNAL_ANALYTICS_TABLE_FIELD_NAMES.some((metricFieldName) => metricFieldName === fieldName);

/** Share links cannot rank internal analytics, so a saved views or downloads sort falls back before the request. */
export const replaceInternalAnalyticsTableSort = (sortFieldName: string, sortDirection: SortDirection) =>
  isInternalAnalyticsTableFieldName(sortFieldName) ? METRIC_SORT_FALLBACK : { sortFieldName, sortDirection };

const isNonEmpty = (value: unknown) =>
  Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '';

const METRIC_SORT_FILTER_KEYS = new Set(['board', 'boardTree', 'library', 'tag', 'type', 'extension', 'customFields']);
const METRIC_SORT_SCOPE_FILTER_FORMS = [
  ['board', ['is']],
  ['boardTree', ['is', 'or']],
  ['library', ['is']],
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isSupportedMetricSortScopeFilter = (filter: unknown, forms: readonly string[]) =>
  isRecord(filter) &&
  forms.some((form) => isNonEmpty(filter[form])) &&
  Object.entries(filter).every(([key, value]) => forms.includes(key) || !isNonEmpty(value));

// The rank path drops a list filter whose first value is empty.
const hasRankableValues = (values: string[] | undefined) => Boolean(values?.[0]);

const isSupportedMetricSortCustomField = ({ filter }: SearchFilterFields) => {
  if ('text' in filter || 'date' in filter) {
    return false;
  }

  return (
    ('is' in filter && filter.is !== undefined) ||
    ('or' in filter && hasRankableValues(filter.or)) ||
    ('and' in filter && hasRankableValues(filter.and)) ||
    ('not' in filter && hasRankableValues(filter.not)) ||
    ('notSet' in filter && filter.notSet === true)
  );
};

/** The rank path rejects these table states with a 400, so the table falls back before issuing the request. */
export const isInternalAnalyticsMetricSortCompatible = (
  params: Pick<ClipsListOptions, 'search' | 'semantic' | 'hybrid' | 'filters' | 'boardId'>,
) => {
  const filters = params.filters ?? {};
  const hasUnsupportedSearch = isNonEmpty(params.search) || params.semantic === true || params.hybrid === true;
  const hasUnsupportedFilter = Object.entries(filters).some(
    ([key, value]) => !METRIC_SORT_FILTER_KEYS.has(key) && isNonEmpty(value),
  );
  const hasUnsupportedScopeFilter = METRIC_SORT_SCOPE_FILTER_FORMS.some(
    ([key, forms]) => isNonEmpty(filters[key]) && !isSupportedMetricSortScopeFilter(filters[key], forms),
  );
  const hasConflictingBoardTreeScope = isNonEmpty(filters.boardTree) && isNonEmpty(filters.board);
  const hasConflictingLibraryScope =
    isNonEmpty(filters.library) &&
    (isNonEmpty(filters.board) || isNonEmpty(filters.boardTree) || isNonEmpty(params.boardId));
  const hasUnsupportedCustomFields =
    filters.customFields?.some(
      (field) => !isCustomFieldValueFilter(field) || !isSupportedMetricSortCustomField(field),
    ) ?? false;

  return !(
    hasUnsupportedSearch ||
    hasUnsupportedFilter ||
    hasUnsupportedScopeFilter ||
    hasConflictingBoardTreeScope ||
    hasConflictingLibraryScope ||
    hasUnsupportedCustomFields
  );
};

const RETENTION_WINDOW_DAYS = 365;

export const getInternalAnalyticsTableWindow = (
  today = new Date(),
): NonNullable<ClipsListOptions['metricsWindow']> => ({
  start: toDateOnly(subDays(today, RETENTION_WINDOW_DAYS - 1)),
  end: toDateOnly(today),
});

type ResolveInternalAnalyticsTableRequestParams = {
  canViewMetrics: boolean;
  hasResolvedMetricsAccess: boolean;
  hasVisibleMetricFields: boolean;
  isMetricSortCompatible: boolean;
  sortFieldName: ViewField['name'];
  sortDirection: SortDirection;
  window: NonNullable<ClipsListOptions['metricsWindow']>;
};

export const resolveInternalAnalyticsTableRequest = ({
  canViewMetrics,
  hasResolvedMetricsAccess,
  hasVisibleMetricFields,
  isMetricSortCompatible,
  sortFieldName,
  sortDirection,
  window,
}: ResolveInternalAnalyticsTableRequestParams) => {
  const isRequestedMetricSort = isInternalAnalyticsTableFieldName(sortFieldName);
  const isRankedMetricSort = canViewMetrics && isRequestedMetricSort && isMetricSortCompatible;
  const shouldFallbackSort = isRequestedMetricSort && !isRankedMetricSort;

  return {
    isIncompatibleMetricSort: canViewMetrics && isRequestedMetricSort && !isMetricSortCompatible,
    isWaitingForMetricsAccess: !hasResolvedMetricsAccess && (hasVisibleMetricFields || isRequestedMetricSort),
    metricsWindow: canViewMetrics && (hasVisibleMetricFields || isRankedMetricSort) ? window : undefined,
    ...(shouldFallbackSort ? METRIC_SORT_FALLBACK : { sortFieldName, sortDirection }),
  };
};
